/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { ReactNode, useEffect, useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import { IconButton, Stack, Tooltip } from "@mui/material";
import MicLevel from "./MicLevel";
import { VolumeUp } from "@mui/icons-material";

type LegalbirdCallProps = {
  children: ReactNode;
};

const LegalbirdCall = ({ children }: LegalbirdCallProps) => {
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [isActive, setIsActive] = useState(false);

  // The audio context belongs to this mounted call dialog, including while minimized.
  useEffect(() => () => {
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(console.error);
    }
  }, [audioContext]);

  const handleAudioContextStart = async () => {
    if (!audioContext) {
      const newAudioContext = new AudioContext();

      setAudioContext(newAudioContext);

      newAudioContext.resume().then(() => {
        setIsActive(true);
      });
    } else {
      if (audioContext.state === "suspended") {
        audioContext.resume().then(() => {
          setIsActive(true);
        });
      }
    }
  };

  const playSoundTest = () => {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    // Use a sine wave for a smooth sound
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5 note

    // Fade out the sound gently
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime); // volume
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.onended = () => {
      ctx.close().catch(console.error);
    };
    oscillator.start();
    oscillator.stop(ctx.currentTime + 1);
  };

  return (
    <Stack sx={{
      alignItems: "center"
    }}>
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          justifyContent: "center",
          alignItems: "center"
        }}>
        {!isActive && (
          <IconButton color={"default"} onClick={handleAudioContextStart}>
            <MicIcon fontSize={"large"} />
          </IconButton>
        )}
        {isActive && <MicLevel />}
        <Tooltip title={"Lautsprecher testen"}>
          <IconButton color={"default"} onClick={playSoundTest} sx={{ width: "51px", height: "51px" }}>
            <VolumeUp />
          </IconButton>
        </Tooltip>
      </Stack>
      {isActive && children}
      {!isActive && (
        <p className={"center"}>Bitte Mikrofon-Button drücken, um das Mikrofon für den Anruf zu aktivieren</p>
      )}
    </Stack>
  );
};

export default LegalbirdCall;
